import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import requestEsriModules from "@mrwa/rime-replacement/infrastructure/esriModules";
import { isMatchingLayer } from "@mrwa/rime-replacement/esriControls/controls/layer/layerSynchronisationImpl";

class irisRoadData {
  constructor() {
    this.isInitialised = false;
    this.currentRoadData = null;
    this.irisRoadFeatures = null;
    this.irisDataChanged = [];
  }

  initialise(irisRoadLayerConfig, playerContext) {
    if (this.isInitialised) return;
    this.isInitialised = true;
    this.irisRoadLayerConfig = irisRoadLayerConfig;
    this.playerContext = playerContext;

    this.playerContext.onGpsDataChanged(this.gpsDataChanged.bind(this));
    this.playerContext.onGpsFileChanged(this.gpsFileChanged.bind(this));
  }

  onirisDataChanged(callback) {
    this.irisDataChanged.push(callback);
  }

  notifySubscribers() {
    this.irisDataChanged.forEach((callback) => {
      callback(this.currentRoadData);
    });
  }

  async gpsFileChanged() {
    this.irisRoadFeatures = null; //ensures irisRoadFeatures is null
    try {
      //get the layer that we're going to use for IRIS data
      const { uniqueIdentifier, subLayerId } = this.irisRoadLayerConfig;
      const rimeLayer = mapHelper.layers.find((layer) =>
        isMatchingLayer({ uniqueIdentifier, subLayerId }, layer)
      );
      if (!rimeLayer) return;

      //query IRIS data for the route
      const bufferedGeometry = await this.playerContext.gpsInformation
        .bufferedGeometry;
      const query = {
        geometry: bufferedGeometry,
        spatialRelationship: "intersects",
        outFields: ["*"],
        returnGeometry: true,
        returnM: true,
      };
      this.irisRoadFeatures = (
        await rimeLayer.layer.queryFeatures(query)
      ).features;
      //initialse display for current gps data.
      this.gpsDataChanged(this.playerContext.gpsInformation.current);
    } catch {
      console.log("error with getting iris road data");
    }
  }

  async gpsDataChanged(gpsData) {
    if (!this.irisRoadFeatures) return;
    if (!gpsData || !gpsData.isValidGeometry) {
      this.currentRoadData = null;
      this.notifySubscribers();
      return;
    }

    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });

    const closure = this;
    async function determineClosestFeature() {
      const currentPoint = await gpsData.projectedGeometry;

      const distances = await Promise.all(
        closure.irisRoadFeatures.map(
          async (feature) =>
            await GeometryEngine.distance(currentPoint, feature.geometry)
        )
      );
      const roadFeature =
        closure.irisRoadFeatures[distances.indexOf(Math.min(...distances))];
      //nearestCoordinate returns the point on the line that's closest to the currentPoint, along with the index of the vertex before that point
      const nearestCoordinate = await GeometryEngine.nearestCoordinate(
        roadFeature.geometry,
        currentPoint
      );

      return { roadFeature, nearestCoordinate };
    }

    async function determineSlk(roadFeature, nearestCoordinate) {
      const roadFeatureVertices = roadFeature.geometry.paths.flat();
      const toPoint = (vertexIndex) => {
        const vertex = roadFeatureVertices[vertexIndex];
        return {
          type: "point",
          x: vertex[0],
          y: vertex[1],
          m: vertex[2],
          spatialReference: mapHelper.mapView.spatialReference,
        };
      };
      const segmentStart = toPoint(nearestCoordinate.vertexIndex);
      const segmentEnd = toPoint(nearestCoordinate.vertexIndex + 1);
      const segmentLength = await GeometryEngine.distance(
        segmentStart,
        segmentEnd
      );
      const pointDistance = await GeometryEngine.distance(
        segmentStart,
        nearestCoordinate.coordinate
      );
      return (
        (segmentEnd.m - segmentStart.m) * (pointDistance / segmentLength) +
        segmentStart.m
      );
    }

    const { roadFeature, nearestCoordinate } = await determineClosestFeature();
    const slk = await determineSlk(roadFeature, nearestCoordinate);
    const { roadNoAttribute, roadNameAttribute, cwyAttribute } =
      this.irisRoadLayerConfig;
    this.currentRoadData = {
      roadNo: roadFeature.attributes[roadNoAttribute],
      roadName: roadFeature.attributes[roadNameAttribute],
      cwy: roadFeature.attributes[cwyAttribute],
      slk: slk,
    };
    this.notifySubscribers();
  }
}

export default new irisRoadData();
